export const PLACEMENTS = {
  home_carousel: "福利专区推荐图",
  tabbar_banner: "福利专区宽幅图",
} as const;
export type Placement = keyof typeof PLACEMENTS;
export interface Creative {
  placement: Placement;
  image_url: string;
  sort_order: number;
}
export interface Payload {
  title: string;
  market_id: "CN";
  link_url: string;
  attribution_enabled?: boolean;
  click_id_param?: string;
  start_at: string;
  end_at: string;
  placements: Creative[];
}
export interface Ad {
  agent_name?: string;
  id: number;
  readonly channel_code: string;
  agent_id: number;
  version: number;
  current_revision_id: number;
  approved_revision_id: number;
  status: "draft" | "pending_review" | "approved" | "rejected";
  paused: boolean;
  master_paused: boolean;
  archived: boolean;
  payload: Payload;
  approved_payload?: Payload;
  created_at: string;
  updated_at: string;
}
export interface Revision {
  id: number;
  revision_no: number;
  payload: Payload;
  change_note: string;
  created_by_kind: string;
  created_at: string;
}
export interface Review {
  id: number;
  revision_id: number;
  decision: string;
  note: string;
  created_at: string;
}
export interface Detail {
  ad: Ad;
  revisions: Revision[];
  reviews: Review[];
}
export interface Slot {
  placement: Placement;
  market_id: "CN";
  enabled: boolean;
  interval_seconds: number;
  version: number;
}
export interface Metrics {
  impressions: number;
  impression_users: number;
  clicks: number;
  click_users: number;
  ctr: number;
}
export interface ReportRow extends Metrics {
  agent_name?: string;
  date: string;
  ad_id: number;
  agent_id: number;
  revision_id: number;
  placement: Placement;
  title: string;
}
export interface Report {
  start_date: string;
  end_date: string;
  totals: Metrics;
  items: ReportRow[];
}
export const ATTRIBUTION_STATUS = {
  linked: "已标记",
  disabled: "未开启",
  not_authorized: "历史未标记",
} as const;
export const REDIRECT_STATUS = {
  pending: "待跳转",
  redirected: "已响应跳转",
  expired: "链接已过期",
} as const;
export interface ClickRecord {
  click_id: string;
  readonly channel_code?: string | null;
  ad_id: number;
  agent_id: number;
  revision_id: number;
  placement: Placement;
  title: string;
  clicked_at: string;
  redirected_at: string | null;
  expires_at: string;
  attribution_status: keyof typeof ATTRIBUTION_STATUS;
  redirect_status: keyof typeof REDIRECT_STATUS;
  target_host: string;
}
export interface ClickReport {
  start_date: string;
  end_date: string;
  items: ClickRecord[];
  total: number;
  page: number;
  page_size: number;
  totals: {
    click_records: number;
    click_users: number;
    attribution_applied: number;
    redirect_responses: number;
  };
}
export type Action =
  | "submit"
  | "withdraw"
  | "pause"
  | "resume"
  | "archive"
  | "master_pause"
  | "master_resume";
export const STATUS = {
  draft: "草稿",
  pending_review: "待审核",
  approved: "已批准",
  rejected: "已驳回",
};
export function chinaInput(value: string) {
  const time = new Date(value).getTime();
  return Number.isFinite(time)
    ? new Date(time + 8 * 3600000).toISOString().slice(0, 16)
    : "";
}
export function chinaTimestamp(value: string) {
  return value ? `${value}:00+08:00` : "";
}
export function chinaDisplay(value: string) {
  return chinaInput(value).replace("T", " ") || "—";
}
export function publicUrl(value: string) {
  try {
    const u = new URL(value);
    return (
      ["http:", "https:"].includes(u.protocol) && !u.username && !u.password
    );
  } catch {
    return false;
  }
}
export function creativeUrl(value: string) {
  return (
    /^\/api\/v1\/public\/ad-creatives\/[a-f0-9]{32}$/.test(value) ||
    publicUrl(value)
  );
}
export function validatePayload(p: Payload) {
  if (!p.title.trim()) return "请填写广告名称";
  if (!publicUrl(p.link_url)) return "请填写有效的 HTTP(S) 落地页链接";
  if (p.attribution_enabled) {
    const parameter = p.click_id_param ?? "channel_id";
    if (!/^[A-Za-z][A-Za-z0-9_]{0,31}$/.test(parameter))
      return "渠道参数名须以英文字母开头，仅含字母、数字、下划线，最多 32 位";
    if (new URL(p.link_url).searchParams.has(parameter))
      return "落地页链接已包含同名渠道参数，请移除该参数或更换参数名，避免覆盖";
  }
  if (
    !Number.isFinite(Date.parse(p.start_at)) ||
    !Number.isFinite(Date.parse(p.end_at)) ||
    Date.parse(p.end_at) <= Date.parse(p.start_at)
  )
    return "结束时间必须晚于开始时间";
  if (!p.placements.length) return "请至少选择一个广告位";
  for (const item of p.placements) {
    if (!item.image_url.trim())
      return `${PLACEMENTS[item.placement]}需要上传图片`;
    if (item.image_url && !creativeUrl(item.image_url))
      return "素材图片须为有效的 HTTP(S) 链接";
    if (!Number.isInteger(item.sort_order) || item.sort_order < 0)
      return "排序须为非负整数";
  }
  return "";
}
export function deliveryState(ad: Ad, now = Date.now()) {
  if (ad.archived) return "已归档";
  if (ad.master_paused) return "总后台已暂停";
  if (ad.paused) return "已暂停";
  if (!ad.approved_revision_id || !ad.approved_payload) return "尚未发布";
  if (now < Date.parse(ad.approved_payload.start_at)) return "待开始";
  if (now >= Date.parse(ad.approved_payload.end_at)) return "已结束";
  return "投放中（受广告位开关控制）";
}
// Counts are additive. UV is deliberately not added across rows: only the server
// can deduplicate people across revisions, placements, and dates.
export function groupCounts(rows: ReportRow[], key: "date" | "placement") {
  const grouped = new Map<
    string,
    { key: string; impressions: number; clicks: number; ctr: number }
  >();
  for (const row of rows) {
    const name = row[key];
    const g = grouped.get(name) ?? {
      key: name,
      impressions: 0,
      clicks: 0,
      ctr: 0,
    };
    g.impressions += row.impressions;
    g.clicks += row.clicks;
    g.ctr = g.impressions ? g.clicks / g.impressions : 0;
    grouped.set(name, g);
  }
  return [...grouped.values()].sort((a, b) => a.key.localeCompare(b.key));
}
